import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { TimedRequestCache } from 'src/app/services/timed-request-cache';
import {
  DemandDetail,
  DemandDetailPageData,
  DemandListItem,
  DemandSource,
  DemandServiceType,
  DemandSpecialty,
  DemandStatus,
} from 'src/assets/types/interface/demand.interface';
import { PaginatorInterface } from 'src/assets/types/interface/table-items.interface';
import { environment } from 'src/environments/environment';
import { PayloadUpload } from '../photos/photos.service';

export interface DemandResponse {
  uuid?: string;
}

export interface DemandRequestOptions {
  types: DemandServiceType[];
  specialties: DemandSpecialty[];
}

export interface ManagementCompanyOption {
  id: number;
  uuid: string;
  name: string;
  description?: string | null;
  document?: string | null;
  contactEmail?: string | null;
}

export interface DemandDecisionPayload {
  action: 'APPROVE' | 'REJECT';
  reason?: string;
}

export interface DemandListFilters {
  userName?: string;
  companyName?: string;
  statusName?: string | string[];
  source?: DemandSource | string;
  search?: string;
  serviceTypeUuid?: string | string[] | null;
  serviceTypeUuids?: string | string[] | null;
  specialtyUuids?: string[] | string;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export interface DemandFilterOptions {
  statuses: DemandStatus[];
  serviceTypes: DemandServiceType[];
  specialties: Array<Pick<DemandSpecialty, 'uuid' | 'name'>>;
}

export interface DemandListPageData extends PaginatorInterface<DemandListItem> {
  filterOptions: DemandFilterOptions;
}

@Injectable({
  providedIn: 'root',
})
export class DemandService {
  private readonly apiUrl = environment.WO_API_URL;
  private readonly detailCache = new TimedRequestCache<DemandDetailPageData>();

  constructor(private http: HttpClient) {}

  getRequestOptions(): Observable<DemandRequestOptions> {
    const accessToken = localStorage.getItem('access_token') ?? '';
    return this.http.get<DemandRequestOptions>(
      `${this.apiUrl}/demand/request-options`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
  }

  createDemand(
    companyUuid: string,
    data: any,
    payloadImages?: PayloadUpload | null,
  ) {
    const access_token = localStorage.getItem('access_token');
    const formData = new FormData();

    formData.append('demand', JSON.stringify(data));

    payloadImages?.files?.forEach((file, index) => {
      formData.append('files', file);
      formData.append(
        'descriptions',
        payloadImages.descriptions?.[index] ?? '',
      );
    });

    return this.http.post<DemandResponse>(
      `${this.apiUrl}/demand/${companyUuid}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      },
    );
  }

  getDemandStatuses(): Observable<DemandStatus[]> {
    const access_token = localStorage.getItem('access_token');
    return this.http.get<DemandStatus[]>(`${this.apiUrl}/demand/status`, {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    });
  }

  getDemandSpecialties(): Observable<DemandSpecialty[]> {
    const access_token = localStorage.getItem('access_token');
    return this.http.get<DemandSpecialty[]>(
      `${this.apiUrl}/service/specialties`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      },
    );
  }

  getDemandTypes(): Observable<DemandServiceType[]> {
    const access_token = localStorage.getItem('access_token');
    return this.http.get<DemandServiceType[]>(`${this.apiUrl}/service/types`, {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    });
  }

  ruleIsAlreadyConfigured(): Observable<any> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.get<any>(`${this.apiUrl}/demand/can-open`, {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    });
  }

  getManagementCompanies(): Observable<ManagementCompanyOption[]> {
    const access_token = localStorage.getItem('access_token') ?? '';

    return this.http.get<ManagementCompanyOption[]>(
      `${this.apiUrl}/management-company`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      },
    );
  }

  getAllDemandsWithPagination(
    take: number,
    page: number,
    filters?: DemandListFilters,
  ): Observable<DemandListPageData> {
    const access_token = localStorage.getItem('access_token') ?? '';

    let params = new HttpParams();
    const setParam = (name: string, value?: string | string[] | null): void => {
      const normalizedValue = Array.isArray(value) ? value.join(',') : value;

      if (normalizedValue) {
        params = params.set(name, normalizedValue);
      }
    };

    setParam('user', filters?.userName);
    setParam('company', filters?.companyName);
    setParam('status', filters?.statusName);
    setParam('source', filters?.source);
    setParam('search', filters?.search);
    setParam('serviceTypeUuid', filters?.serviceTypeUuid);
    setParam('serviceTypeUuids', filters?.serviceTypeUuids);
    setParam('specialtyUuids', filters?.specialtyUuids);
    setParam('dateFrom', filters?.dateFrom);
    setParam('dateTo', filters?.dateTo);

    return this.http.get<DemandListPageData>(
      `${this.apiUrl}/demand/limit/${take}/page/${page}`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
        params,
      },
    );
  }

  getDemandByUuid(demandUuid: string): Observable<DemandDetailPageData> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.detailCache.get(`${access_token}:${demandUuid}`, () =>
      this.http.get<DemandDetailPageData>(
        `${this.apiUrl}/demand/one/${demandUuid}`,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      ),
    );
  }

  refreshDemandByUuid(demandUuid: string): Observable<DemandDetailPageData> {
    const access_token = localStorage.getItem('access_token') ?? '';
    this.detailCache.invalidate(`${access_token}:${demandUuid}`);

    return this.getDemandByUuid(demandUuid);
  }

  decideInternalDemand(
    demandUuid: string,
    payload: DemandDecisionPayload,
  ): Observable<DemandDetail> {
    const access_token = localStorage.getItem('access_token') ?? '';

    return this.http
      .patch<DemandDetail>(
        `${this.apiUrl}/demand/${demandUuid}/decision`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      )
      .pipe(tap(() => this.detailCache.clear()));
  }
}
